import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaDatabase, FaServer, FaDownload, FaTrash, FaPlus, FaEye, FaEyeSlash, FaLock, FaSpinner, FaQuestionCircle, FaTimes, FaFileExport, FaTable, FaCloud, FaExternalLinkAlt, FaCheck } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

const BackupManagement = () => {
  const { apiCall } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [backupPassword, setBackupPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('database_dump');
  const [createOptions, setCreateOptions] = useState({
    includeData: true,
    includeSchema: true,
    includeRoles: true,
    includeTablespaces: true,
    format: 'custom',
    compress: true,
    insertFormat: 'copy' // 'copy' or 'insert'
  });
  const [selectedTables, setSelectedTables] = useState([]);
  const [availableTables, setAvailableTables] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [progressInterval, setProgressInterval] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);

  useEffect(() => {
    // Require password authentication on initial load
    setShowPasswordModal(true);
    fetchTables();
  }, []);

  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/backup');
      if (response.success) {
        setBackups(response.backups || []);
      } else {
        toast.error('Failed to fetch backups');
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Failed to fetch backups');
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await apiCall('/backup/tables');
      if (response.success) {
        setAvailableTables(response.tables || []);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const verifyBackupPassword = async (password) => {
    try {
      const response = await apiCall('/backup/verify-password', {
        method: 'POST',
        body: JSON.stringify({
          backupPassword: password
        })
      });
      
      if (response.success) {
        setIsAuthenticated(true);
        setShowPasswordModal(false);
        
        // Fetch backups after successful authentication
        await fetchBackups();
        
        if (pendingAction) {
          pendingAction();
          setPendingAction(null);
        }
        
        toast.success('Access granted to backup system');
      } else {
        toast.error('Invalid backup access password');
        setBackupPassword('');
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      toast.error('Failed to verify password');
    }
  };

  const requireBackupPassword = (action) => {
    if (isAuthenticated) {
      action();
    } else {
      setPendingAction(() => action);
      setShowPasswordModal(true);
    }
  };

  const createBackup = async () => {
    if (!backupPassword) {
      toast.error('Please enter backup password');
      return;
    }

    try {
      setCreating(true);
      let endpoint, payload;

      if (createType === 'database_dump') {
        endpoint = '/backup/create-database-dump';
        payload = {
          backupPassword,
          options: {
            includeData: createOptions.includeData,
            includeSchema: createOptions.includeSchema,
            format: createOptions.format,
            compress: createOptions.compress
          }
        };
      } else if (createType === 'cluster_dump') {
        endpoint = '/backup/create-cluster-dump';
        payload = {
          backupPassword,
          options: {
            includeRoles: createOptions.includeRoles,
            includeTablespaces: createOptions.includeTablespaces,
            includeData: createOptions.includeData
          }
        };
      } else if (createType === 'table_export') {
        if (selectedTables.length === 0) {
          toast.error('Please select at least one table to export');
          return;
        }
        endpoint = '/backup/export-tables';
        payload = {
          backupPassword,
          tables: selectedTables,
          options: {
            includeData: createOptions.includeData,
            includeSchema: createOptions.includeSchema,
            insertFormat: createOptions.insertFormat
          }
        };
      }

      const response = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      if (response.success) {
        toast.success(response.message);
        setShowCreateModal(false);
        
        // Start polling for progress
        const backupId = response.backupId;
        startProgressPolling(backupId);
      } else {
        toast.error(response.message || 'Failed to create backup');
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const startProgressPolling = (backupId) => {
    const interval = setInterval(async () => {
      try {
        const response = await apiCall(`/backup/${backupId}/status`);
        if (response.success) {
          setProgress(response.backup);
          
          if (response.backup.status === 'completed' || response.backup.status === 'failed') {
            clearInterval(interval);
            setProgressInterval(null);
            setProgress(null);
            fetchBackups(); // Refresh the list
            
            if (response.backup.status === 'completed') {
              toast.success('Backup completed successfully!');
            } else {
              toast.error('Backup failed');
            }
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
        clearInterval(interval);
        setProgressInterval(null);
        setProgress(null);
      }
    }, 2000);
    
    setProgressInterval(interval);
  };

  const downloadBackup = async (backupId, backupName) => {
    try {
      const response = await apiCall(`/backup/${backupId}/download`);
      
      if (response.success && response.downloadUrl) {
        window.open(response.downloadUrl, '_blank');
        toast.success('Download started');
      } else {
        toast.error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup');
    }
  };

  const openS3Location = async (backup) => {
    try {
      const response = await apiCall(`/backup/${backup.id}/s3-url`);
      if (response.success && response.signedUrl) {
        window.open(response.signedUrl, '_blank');
      } else {
        toast.error('Failed to get S3 location');
      }
    } catch (error) {
      console.error('Error getting S3 location:', error);
      toast.error('Failed to access S3 location');
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) {
      return;
    }

    try {
      const response = await apiCall(`/backup/${backupId}`, {
        method: 'DELETE'
      });
      if (response.success) {
        toast.success('Backup deleted successfully');
        fetchBackups();
      } else {
        toast.error('Failed to delete backup');
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getBackupTypeIcon = (type) => {
    switch (type) {
      case 'database_dump':
        return <FaDatabase className="text-blue-500" />;
      case 'cluster_dump':
        return <FaServer className="text-purple-500" />;
      case 'table_export':
        return <FaTable className="text-green-500" />;
      default:
        return <FaDatabase className="text-gray-500" />;
    }
  };

  const getBackupTypeName = (type) => {
    switch (type) {
      case 'database_dump':
        return 'Database Dump';
      case 'cluster_dump':
        return 'Cluster Dump';
      case 'table_export':
        return 'Table Export';
      default:
        return type;
    }
  };

  const handleTableSelection = (tableName) => {
    setSelectedTables(prev => {
      if (prev.includes(tableName)) {
        return prev.filter(t => t !== tableName);
      } else {
        return [...prev, tableName];
      }
    });
  };

  const selectAllTables = () => {
    setSelectedTables(availableTables);
  };

  const clearTableSelection = () => {
    setSelectedTables([]);
  };

  // Password Modal
  if (showPasswordModal) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full mb-4">
              <FaLock className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 text-center mb-4">Backup System Access</h3>
            <p className="text-sm text-gray-600 text-center mb-6">
              Enter the backup access password to continue
            </p>
            
            <div className="mb-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={backupPassword}
                  onChange={(e) => setBackupPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && verifyBackupPassword(backupPassword)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder="Enter backup password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? <FaEyeSlash className="h-4 w-4 text-gray-400" /> : <FaEye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setBackupPassword('');
                  setPendingAction(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => verifyBackupPassword(backupPassword)}
                disabled={!backupPassword}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Access
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">PostgreSQL Backup Management</h1>
            <p className="text-gray-600 mt-1">Create and manage database backups, cluster dumps, and table exports</p>
          </div>
          <button
            onClick={() => setShowHelpModal(true)}
            className="text-blue-600 hover:text-blue-800 transition-colors p-2 rounded-full hover:bg-blue-50"
            title="Help & Information"
          >
            <FaQuestionCircle className="h-6 w-6" />
          </button>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchBackups}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {refreshing ? (
              <FaSpinner className="h-4 w-4 animate-spin" />
            ) : (
              <span>🔄</span>
            )}
            <span>Refresh</span>
          </button>
          <button
            onClick={() => requireBackupPassword(() => setShowCreateModal(true))}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <FaPlus className="h-4 w-4" />
            <span>Create Backup</span>
          </button>
        </div>
      </div>

      {/* Progress Indicator */}
      {progress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600"></div>
              <div>
                <h3 className="text-lg font-semibold text-blue-900">{progress.name}</h3>
                <p className="text-blue-700">{getBackupTypeName(progress.type)}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {progress.metadata?.progress || 0}%
              </div>
            </div>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${progress.metadata?.progress || 0}%` }}
            ></div>
          </div>
          <p className="text-sm text-blue-700">
            {progress.metadata?.status || 'Processing...'}
          </p>
        </div>
      )}

      {/* Backups Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Backup History</h2>
        </div>
        
        {backups.length === 0 ? (
          <div className="text-center py-12">
            <FaDatabase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No backups found</h3>
            <p className="text-gray-600">Create your first backup to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {backups.map((backup) => (
                  <tr key={backup.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getBackupTypeIcon(backup.type)}
                        <span className="text-sm font-medium text-gray-900">
                          {getBackupTypeName(backup.type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{backup.name}</div>
                        {backup.description && (
                          <div className="text-sm text-gray-500">{backup.description}</div>
                        )}
                        {backup.metadata?.tables && (
                          <div className="text-xs text-blue-600 mt-1">
                            Tables: {backup.metadata.tables.join(', ')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        backup.status === 'completed' ? 'bg-green-100 text-green-800' :
                        backup.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                        backup.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {backup.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatFileSize(backup.file_size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDistanceToNow(new Date(backup.backup_date), { addSuffix: true })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {backup.status === 'completed' && (
                          <>
                            <button
                              onClick={() => downloadBackup(backup.id, backup.name)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title="Download"
                            >
                              <FaDownload className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openS3Location(backup)}
                              className="text-green-600 hover:text-green-900 p-1 rounded"
                              title="Open S3 Location"
                            >
                              <FaExternalLinkAlt className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => requireBackupPassword(() => deleteBackup(backup.id))}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Backup Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">Create New Backup</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Backup Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Backup Type</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div 
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                        createType === 'database_dump' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setCreateType('database_dump')}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <FaDatabase className="text-blue-500" />
                        <span className="font-medium">Database Dump</span>
                      </div>
                      <p className="text-sm text-gray-600">Export database schema and data using pg_dump</p>
                    </div>
                    
                    <div 
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                        createType === 'cluster_dump' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setCreateType('cluster_dump')}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <FaServer className="text-purple-500" />
                        <span className="font-medium">Cluster Dump</span>
                      </div>
                      <p className="text-sm text-gray-600">Export entire cluster including roles and tablespaces</p>
                    </div>
                    
                    <div 
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                        createType === 'table_export' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setCreateType('table_export')}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <FaTable className="text-green-500" />
                        <span className="font-medium">Table Export</span>
                      </div>
                      <p className="text-sm text-gray-600">Export specific tables to SQL file</p>
                    </div>
                  </div>
                </div>

                {/* Table Selection for Table Export */}
                {createType === 'table_export' && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <label className="block text-sm font-medium text-gray-700">Select Tables</label>
                      <div className="space-x-2">
                        <button
                          type="button"
                          onClick={selectAllTables}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={clearTableSelection}
                          className="text-sm text-gray-600 hover:text-gray-800"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {availableTables.map((table) => (
                          <label key={table} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedTables.includes(table)}
                              onChange={() => handleTableSelection(table)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{table}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      {selectedTables.length} table(s) selected
                    </p>
                  </div>
                )}

                {/* Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Options</label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="includeSchema"
                        checked={createOptions.includeSchema}
                        onChange={(e) => setCreateOptions(prev => ({ ...prev, includeSchema: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="includeSchema" className="text-sm text-gray-700">Include Schema</label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="includeData"
                        checked={createOptions.includeData}
                        onChange={(e) => setCreateOptions(prev => ({ ...prev, includeData: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="includeData" className="text-sm text-gray-700">Include Data</label>
                    </div>

                    {createType === 'table_export' && createOptions.includeData && (
                      <div>
                        <label className="block text-sm text-gray-700 mb-2">Data Format</label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-3">
                            <input
                              type="radio"
                              id="formatCopy"
                              name="insertFormat"
                              value="copy"
                              checked={createOptions.insertFormat === 'copy'}
                              onChange={(e) => setCreateOptions(prev => ({ ...prev, insertFormat: e.target.value }))}
                              className="border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label htmlFor="formatCopy" className="text-sm text-gray-700">
                              COPY Format (recommended - faster, more efficient)
                            </label>
                          </div>
                          <div className="flex items-center space-x-3">
                            <input
                              type="radio"
                              id="formatInsert"
                              name="insertFormat"
                              value="insert"
                              checked={createOptions.insertFormat === 'insert'}
                              onChange={(e) => setCreateOptions(prev => ({ ...prev, insertFormat: e.target.value }))}
                              className="border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label htmlFor="formatInsert" className="text-sm text-gray-700">
                              INSERT Statements (slower, but more portable)
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {createType === 'cluster_dump' && (
                      <>
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="includeRoles"
                            checked={createOptions.includeRoles}
                            onChange={(e) => setCreateOptions(prev => ({ ...prev, includeRoles: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor="includeRoles" className="text-sm text-gray-700">Include Roles</label>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="includeTablespaces"
                            checked={createOptions.includeTablespaces}
                            onChange={(e) => setCreateOptions(prev => ({ ...prev, includeTablespaces: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor="includeTablespaces" className="text-sm text-gray-700">Include Tablespaces</label>
                        </div>
                      </>
                    )}

                    {createType === 'database_dump' && (
                      <>
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="compress"
                            checked={createOptions.compress}
                            onChange={(e) => setCreateOptions(prev => ({ ...prev, compress: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor="compress" className="text-sm text-gray-700">Compress Output</label>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Format</label>
                          <select
                            value={createOptions.format}
                            onChange={(e) => setCreateOptions(prev => ({ ...prev, format: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="custom">Custom (recommended)</option>
                            <option value="plain">Plain SQL</option>
                            <option value="directory">Directory</option>
                            <option value="tar">TAR Archive</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Backup Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={backupPassword}
                      onChange={(e) => setBackupPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                      placeholder="Enter backup password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? <FaEyeSlash className="h-4 w-4 text-gray-400" /> : <FaEye className="h-4 w-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createBackup}
                  disabled={creating || !backupPassword || (createType === 'table_export' && selectedTables.length === 0)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {creating && <FaSpinner className="h-4 w-4 animate-spin" />}
                  <span>Create Backup</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">PostgreSQL Backup Help</h3>
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-blue-900 mb-2">Overview</h4>
                  <p className="text-blue-800">
                    This system provides comprehensive PostgreSQL backup capabilities using pg_dump and pg_dumpall utilities.
                    All backups are stored securely in Amazon S3 for reliable access and long-term retention.
                  </p>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Backup Types</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <FaDatabase className="h-5 w-5 text-blue-600 mr-2" />
                        <h5 className="font-semibold text-blue-900">Database Dump</h5>
                      </div>
                      <p className="text-blue-800 text-sm">
                        Uses pg_dump to create a backup of the current database including schema and data.
                        Supports multiple formats and compression options.
                      </p>
                    </div>
                    
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <FaServer className="h-5 w-5 text-purple-600 mr-2" />
                        <h5 className="font-semibold text-purple-900">Cluster Dump</h5>
                      </div>
                      <p className="text-purple-800 text-sm">
                        Uses pg_dumpall to create a complete backup of the entire PostgreSQL cluster,
                        including all databases, roles, and tablespaces.
                      </p>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <FaTable className="h-5 w-5 text-green-600 mr-2" />
                        <h5 className="font-semibold text-green-900">Table Export</h5>
                      </div>
                      <p className="text-green-800 text-sm">
                        Exports specific tables to SQL files. Useful for partial backups
                        or migrating specific data sets.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Storage & Access</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <FaCloud className="h-4 w-4 text-blue-500 mr-2 mt-0.5" />
                        <span>All backups are automatically uploaded to Amazon S3 for secure storage</span>
                      </li>
                      <li className="flex items-start">
                        <FaDownload className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                        <span>Download backups directly from S3 using signed URLs</span>
                      </li>
                      <li className="flex items-start">
                        <FaExternalLinkAlt className="h-4 w-4 text-purple-500 mr-2 mt-0.5" />
                        <span>Access S3 locations directly for advanced management</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Best Practices</h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <ul className="space-y-2 text-green-800">
                      <li className="flex items-start">
                        <FaCheck className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                        <span>Schedule regular backups during low-traffic periods</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                        <span>Test backup restoration procedures regularly</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                        <span>Keep multiple backup copies with different retention periods</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                        <span>Use cluster dumps for complete disaster recovery scenarios</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupManagement;